import {
  FC,
  useState,
  useEffect,
  useRef,
  memo,
  MutableRefObject,
  MouseEvent,
  TouchEvent
} from 'react';

import clsx from 'clsx';

import Markers from './Markers';

import { getGradient } from '../../../utils';
import { useDragListeners } from '../../../utils/useDragListeners';
import { arraysEqual, shallowEqual } from '../helper';

import { IPropsPanel, TCoords } from './types';
import { RADIALS_POS } from '../constants';

const GradientPanel: FC<IPropsPanel> = ({
  color,
  setColor,
  activeColor,
  setActiveColor,
  setInit,
  format = 'rgb',
  showAlpha = true,
  showGradientResult = true,
  showGradientStops = true,
  showGradientMode = true,
  showGradientAngle = true,
  showGradientPosition = true,
  allowAddGradientStops = true
}) => {
  const angleNode = useRef() as MutableRefObject<HTMLDivElement>;
  const drag = useDragListeners();

  const { stops, gradient, type, modifier } = color;

  const [radialsPosition, setRadialPosition] = useState(RADIALS_POS);

  const onClickMode = () => {
    setInit(false);
    switch (type) {
      case 'linear': {
        const activePos = radialsPosition.find((item) => item.active);
        setColor({
          ...color,
          modifier: activePos?.css || modifier,
          gradient: `${getGradient(
            'radial',
            stops,
            activePos?.css || modifier,
            format,
            showAlpha
          )}`,
          type: 'radial'
        });
        break;
      }

      case 'radial': {
        setColor({
          ...color,
          gradient: `${getGradient('linear', stops, 180, format, showAlpha)}`,
          type: 'linear'
        });
        break;
      }

      default: {
        break;
      }
    }
  };

  const setActiveRadialPosition = (e: MouseEvent) => {
    setInit(false);
    const target = e.target as HTMLElement;
    const pos = target.getAttribute('data-pos');
    const newRadialsPosition = radialsPosition.map((item) => {
      if (item.pos === pos) {
        return {
          ...item,
          active: true
        };
      }

      return {
        ...item,
        active: false
      };
    });

    setRadialPosition(newRadialsPosition);

    const activePos = newRadialsPosition.find((item) => item.active);
    setColor({
      ...color,
      modifier: activePos?.css || modifier,
      gradient: `${getGradient(
        'radial',
        stops,
        activePos?.css || modifier,
        format,
        showAlpha
      )}`
    });
  };

  const removeListeners = () => {
    drag.stop();
  };

  const removeTouchListeners = () => {
    drag.stop();
  };

  const onMouseDown = (e: any) => {
    e.preventDefault();

    setInit(false);

    if (e.button !== 0) return;

    const x = e.clientX;
    const y = e.clientY;
    const shiftKey = e.shiftKey;
    const ctrlKey = e.ctrlKey * 2;

    // `data-mode` no lugar da className: com utilitárias do Tailwind, comparar a string inteira
    // quebraria a cada ajuste de estilo.
    if (e.target.dataset?.mode === undefined && type === 'linear') {
      pointMoveTo({
        x,
        y,
        shiftKey,
        ctrlKey
      });

      drag.start({ mousemove: onDrag, mouseup: onDragEnd });
    }
  };

  const onDrag = (e: any) => {
    const x = e.clientX;
    const y = e.clientY;
    const shiftKey = e.shiftKey;
    const ctrlKey = e.ctrlKey * 2;

    pointMoveTo({
      x,
      y,
      shiftKey,
      ctrlKey
    });
  };

  const onDragEnd = (e: any) => {
    const x = e.clientX;
    const y = e.clientY;
    const shiftKey = e.shiftKey;
    const ctrlKey = e.ctrlKey * 2;

    pointMoveTo({
      x,
      y,
      shiftKey,
      ctrlKey
    });

    removeListeners();
  };

  const onTouchStart = (e: TouchEvent) => {
    setInit(false);

    if (e.cancelable) {
      e.preventDefault();
    }

    if (e.touches.length !== 1) {
      return;
    }

    removeTouchListeners();

    const x = e.targetTouches[0].clientX;
    const y = e.targetTouches[0].clientY;
    const shiftKey = false;
    const ctrlKey = 0;

    pointMoveTo({ x, y, shiftKey, ctrlKey });

    drag.start(
      { touchmove: onTouchMove, touchend: onTouchEnd },
      { passive: false }
    );
  };

  const onTouchMove = (e: any) => {
    if (e.cancelable) {
      e.preventDefault();
    }

    const x = e.targetTouches[0].clientX;
    const y = e.targetTouches[0].clientY;
    const shiftKey = false;
    const ctrlKey = 0;

    pointMoveTo({
      x,
      y,
      shiftKey,
      ctrlKey
    });
  };

  const onTouchEnd = () => {
    removeTouchListeners();
  };

  const pointMoveTo = (coords: TCoords) => {
    const rect = angleNode.current?.getBoundingClientRect();
    if (!rect) return;

    const boxcx = rect.left + rect.width / 2;
    const boxcy = rect.top + rect.height / 2;
    const radians = Math.atan2(coords.x - boxcx, coords.y - boxcy) - Math.PI;
    const degrees = Math.abs((radians * 180) / Math.PI);

    const div = [1, 2, 4][Number(coords.shiftKey || coords.ctrlKey)];
    const newAngle = degrees - (degrees % (45 / div));

    setColor({
      ...color,
      gradient: `${getGradient(type, stops, newAngle, format, showAlpha)}`,
      modifier: newAngle
    });
  };

  useEffect(() => {
    return () => {
      removeListeners();
      removeTouchListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (type === 'radial') {
      const activePos = radialsPosition.find((item) => item.css === modifier);
      setColor({
        ...color,
        modifier: activePos?.css || modifier,
        gradient: `${getGradient(
          'radial',
          stops,
          activePos?.css || modifier,
          format,
          showAlpha
        )}`
      });

      setRadialPosition(
        RADIALS_POS.map((item) => {
          if (item.css === modifier) {
            return {
              ...item,
              active: true
            };
          }

          return {
            ...item,
            active: false
          };
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifier]);

  return (
    <div className='z-[1] flex flex-col'>
      {showGradientResult && (
        <div
          className='group relative mt-[18px] h-[74px] w-full flex-grow rounded-md text-base'
          onMouseDown={showGradientAngle ? onMouseDown : undefined}
          onTouchStart={showGradientAngle ? onTouchStart : undefined}
          style={{ background: gradient }}
        >
          {/* Botão linear/radial. O glifo é o ::before: um traço em 45° no linear, um disco no
              radial. */}
          {showGradientMode && (
            <div
              data-mode={type}
              onClick={() => onClickMode()}
              className={clsx(
                "absolute left-4 top-5 size-8 cursor-pointer rounded-[0.15em] border-2 border-white opacity-25 transition-all duration-300 hover:opacity-100 before:absolute before:inset-0 before:m-auto before:transition-all before:duration-300 before:content-['']",
                type === 'linear' &&
                  'before:h-0.5 before:w-[70%] before:rotate-45 before:rounded-[50em] before:bg-white',
                type === 'radial' &&
                  'before:size-1/2 before:rounded-full before:border-2 before:border-white before:bg-white'
              )}
            />
          )}

          {/* Ponteiro do ângulo: some no radial e acende quando o mouse entra no resultado. */}
          {showGradientAngle && (
            <div
              ref={angleNode}
              className={clsx(
                'absolute inset-0 m-auto size-[0.35em] rounded-full bg-white opacity-25 transition-all duration-300',
                type === 'linear' && 'group-hover:opacity-100'
              )}
              style={{ visibility: type === 'linear' ? 'visible' : 'hidden' }}
            >
              <div
                className='absolute inset-y-0 left-1/2 my-auto h-0.5 w-8 origin-left rounded-[1em] bg-white'
                style={{
                  transform: `rotate(${
                    typeof modifier === 'number'
                      ? modifier - 90 + 'deg'
                      : modifier
                  })`
                }}
              ></div>
            </div>
          )}

          {/* Grade 3x3 de origem do radial. */}
          {showGradientPosition && (
            <div
              className='absolute inset-0 m-auto grid size-20 grid-cols-3 grid-rows-3 transition-all duration-300'
              style={{
                opacity: type === 'radial' ? '1' : '0',
                visibility: type === 'radial' ? 'visible' : 'hidden'
              }}
            >
              {radialsPosition.map((item) => {
                return (
                  <div
                    key={item.pos}
                    data-pos={item.pos}
                    onClick={(e) => setActiveRadialPosition(e)}
                    className={clsx(
                      "relative m-auto size-[15px] border-2 border-transparent transition-all duration-300 before:absolute before:inset-0 before:m-auto before:size-[5px] before:rounded-full before:bg-white before:opacity-25 before:transition-all before:duration-300 before:content-[''] hover:before:opacity-100",
                      item.active
                        ? 'rounded-full border-white before:opacity-100'
                        : 'cursor-pointer'
                    )}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
      {showGradientStops && (
        <Markers
          color={color}
          setColor={setColor}
          activeColor={activeColor}
          setActiveColor={setActiveColor}
          setInit={setInit}
          format={format}
          showAlpha={showAlpha}
          allowAddGradientStops={allowAddGradientStops}
        />
      )}
    </div>
  );
};

const arePropsEqual = (prevProps: any, nextProps: any) => {
  if (
    arraysEqual(prevProps.color.stops, nextProps.color.stops) &&
    prevProps.color.modifier === nextProps.color.modifier &&
    prevProps.color.type === nextProps.color.type &&
    shallowEqual(prevProps.activeColor, nextProps.activeColor)
  ) {
    return true;
  }

  return false;
};

export default memo(GradientPanel, arePropsEqual);
